import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Tabs } from "@/components/ui/Tabs";
import { Switch } from "@/components/ui/Switch";
import { Button } from "@/components/ui/Button";
import { Empresa } from "@/types/empresa";
import { useAuth } from "@/hooks/useAuth";
import { empresaService } from "@/lib/api/empresaService";
import { pagadorasService } from "@/lib/api/pagadorasService";
import { User } from "@/types/user";
import { getAsesoresCached } from "@/hooks/useSharedData";

const InfoIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const UsersIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m8-4a4 4 0 10-8 0 4 4 0 008 0zM6 10a4 4 0 118 0 4 4 0 01-8 0z" />
  </svg>
);

interface EmpresaDetailProps {
  isOpen: boolean;
  onClose: () => void;
  empresa: Empresa | null;
  onUpdate: (empresa: Empresa) => void;
}

export const EmpresaDetail: React.FC<EmpresaDetailProps> = ({ isOpen, onClose, empresa, onUpdate }) => {
  const { isFieldAllowed } = useAuth();
  const canNombre = isFieldAllowed('nombre');
  const canAgrupacion = isFieldAllowed('agrupacion');
  const canEmpresaPagadora = isFieldAllowed('empresaPagadora');
  const canEmpresaSegmentacionCobranza = isFieldAllowed('empresaSegmentacionCobranza');
  const canTipo = isFieldAllowed('tipo');
  const canRegion = isFieldAllowed('region');
  const canAutorizacionDG = isFieldAllowed('autorizacionDG');
  const canVoboRequerido = isFieldAllowed('voboRequerido');
  const canEstatus = isFieldAllowed('estatus');
  const canNivel = isFieldAllowed('nivel');

  const [activeTab, setActiveTab] = useState("info");
  const [asesores, setAsesores] = useState<User[]>([]);
  const [asignados, setAsignados] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingOwner, setUpdatingOwner] = useState<string | null>(null);
  
  // Estado local para edición del formulario
  const [localEmpresa, setLocalEmpresa] = useState<Empresa | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pagadorasMap, setPagadorasMap] = useState<Record<string, string[]>>({});
  const pagadorasList = useMemo(() => Object.keys(pagadorasMap).sort((a, b) => a.localeCompare(b)), [pagadorasMap]);
  const segmentosList = useMemo(() => pagadorasMap[localEmpresa?.empresaPagadora || ''] || [], [pagadorasMap, localEmpresa?.empresaPagadora]);

  useEffect(() => {
    if (!isOpen || !empresa?.id) return;
    setActiveTab("info");
    setError(null);
    setLoading(true);
    setLocalEmpresa({ ...empresa });
    setHasChanges(false);

    pagadorasService.get().then((resp) => {
      if (resp.data && typeof resp.data === "object") {
        setPagadorasMap(resp.data);
      }
    }).catch(() => setPagadorasMap({}));
    
    (async () => {
      try {
        const [u, a] = await Promise.all([
          getAsesoresCached(),
          empresaService.getAsignaciones(empresa.id!),
        ]);
        setAsesores(u);
        setAsignados(a.data || []);
      } catch (e: any) {
        setError(e?.message || "No se pudieron cargar asignaciones.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, empresa?.id]);

  // Sincronizar localEmpresa cuando cambia empresa
  useEffect(() => {
    if (empresa) {
      setLocalEmpresa({ ...empresa });
      setHasChanges(false);
    }
  }, [empresa]);

  const asesoresFiltrados = useMemo(() => {
    const list = asesores.filter((u) => (u.userEstatus || "").toLowerCase() === "activo");
    if (!localEmpresa?.region) return list;
    return list.filter((u) => (u.region || "") === localEmpresa.region);
  }, [asesores, localEmpresa?.region]);

  if (!empresa || !localEmpresa) return null;

  const isAssigned = (ownerID: string) => {
    if (localEmpresa.ownerID && localEmpresa.ownerID === ownerID) return true;
    return asignados.includes(ownerID);
  };

  const toggleAsignacion = async (ownerID: string, next: boolean) => {
    if (!empresa.id) return;
    setError(null);
    setUpdatingOwner(ownerID);
    try {
      const resp = await empresaService.setAsignacion(empresa.id, ownerID, next);
      if (resp.data) setAsignados(resp.data);
      else setError(resp.error || "No se pudo actualizar la asignación.");
    } catch (e: any) {
      setError(e?.message || "Error al actualizar la asignación.");
    } finally {
      setUpdatingOwner(null);
    }
  };

  // Función para actualizar campos del formulario
  const handleFieldChange = (field: keyof Empresa, value: string) => {
    setLocalEmpresa(prev => prev ? { ...prev, [field]: value } : null);
    setHasChanges(true);
  };

  // Guardar cambios
  const handleSave = async () => {
    if (!localEmpresa?.id || !hasChanges) return;
    setIsSaving(true);
    setError(null);
    try {
      const resp = await empresaService.update(localEmpresa.id, localEmpresa);
      if (resp.data) {
        onUpdate(resp.data);
        setHasChanges(false);
      } else {
        setError(resp.error || "Error al guardar los cambios");
      }
    } catch (e: any) {
      setError(e?.message || "Error al guardar");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}  title={`Empresa: ${empresa.nombre}`}>
      <div className="space-y-4">
        <Tabs
          activeTab={activeTab}
          onChange={setActiveTab}
          items={[
            { id: "info", label: "Información", icon: <InfoIcon /> },
            { id: "asignaciones", label: "Asignaciones", icon: <UsersIcon /> },
          ]}
        />

        {error && <div className="p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{error}</div>}

        {activeTab === "info" && (
          <div className="space-y-4">
            {/* Formulario estilo legacy */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {canNombre && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200 w-1/3">Nombre</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localEmpresa.nombre || ''}
                        onChange={(e) => handleFieldChange('nombre', e.target.value)}
                      />
                    </td>
                  </tr>
                  )}

                  {canAgrupacion && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Agrupación</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localEmpresa.agrupacion || ''}
                        onChange={(e) => handleFieldChange('agrupacion', e.target.value)}
                      />
                    </td>
                  </tr>
                  )}

                  {canEmpresaPagadora && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Pagadora</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localEmpresa.empresaPagadora || ''}
                        onChange={(e) => {
                          handleFieldChange('empresaPagadora', e.target.value);
                          const nuevosSegmentos = pagadorasMap[e.target.value] || [];
                          if (!nuevosSegmentos.includes(localEmpresa.empresaSegmentacionCobranza || '')) {
                            handleFieldChange('empresaSegmentacionCobranza', '');
                          }
                        }}
                      >
                        <option value="">Seleccionar pagadora...</option>
                        {pagadorasList.map((nombre) => (
                          <option key={nombre} value={nombre}>{nombre}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  )}

                  {canEmpresaSegmentacionCobranza && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Segmentación de Cobranza</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localEmpresa.empresaSegmentacionCobranza || ''}
                        onChange={(e) => handleFieldChange('empresaSegmentacionCobranza', e.target.value)}
                        disabled={!localEmpresa.empresaPagadora}
                      >
                        <option value="">{localEmpresa.empresaPagadora ? 'Seleccionar segmento...' : 'Selecciona primero una pagadora'}</option>
                        {segmentosList.map((seg) => (
                          <option key={seg} value={seg}>{seg}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  )}

                  {canTipo && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Tipo</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localEmpresa.tipo || ''}
                        onChange={(e) => handleFieldChange('tipo', e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Interna">Interna</option>
                        <option value="Externa">Externa</option>
                      </select>
                    </td>
                  </tr>
                  )}

                  {canRegion && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Región</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localEmpresa.region || ''}
                        onChange={(e) => handleFieldChange('region', e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="CDMX">CDMX</option>
                        <option value="Guadalajara">Guadalajara</option>
                      </select>
                    </td>
                  </tr>
                  )}

                  {canAutorizacionDG && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Autorización DG</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localEmpresa.autorizacionDG || 'Normal'}
                        onChange={(e) => handleFieldChange('autorizacionDG', e.target.value)}
                      >
                        <option value="Normal">Normal</option>
                        <option value="Estricta">Estricta</option>
                      </select>
                    </td>
                  </tr>
                  )}

                  {canVoboRequerido && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Se Requiere VoBo</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localEmpresa.voboRequerido || ''}
                        onChange={(e) => handleFieldChange('voboRequerido', e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Sí">Sí</option>
                        <option value="No">No</option>
                        <option value="Sólo Administrativos">Sólo Administrativos</option>
                      </select>
                    </td>
                  </tr>
                  )}

                  {canEstatus && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Estatus</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localEmpresa.estatus || 'activa'}
                        onChange={(e) => handleFieldChange('estatus', e.target.value)}
                      >
                        <option value="activa">Activa</option>
                        <option value="congelada">Congelada</option>
                        <option value="baja">Baja</option>
                      </select>
                    </td>
                  </tr>
                  )}

                  {canNivel && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Nivel</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localEmpresa.nivel || ''}
                        onChange={(e) => handleFieldChange('nivel', e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Riesgo 1">Riesgo 1</option>
                        <option value="Riesgo 2">Riesgo 2</option>
                        <option value="Riesgo 3">Riesgo 3</option>
                        <option value="Riesgo 4">Riesgo 4</option>
                        <option value="Riesgo 5">Riesgo 5</option>
                      </select>
                    </td>
                  </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Botón guardar */}
            {hasChanges && (
              <div className="flex justify-end pt-4 border-t border-gray-100">
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            )}
          </div>
        )}

        {activeTab === "asignaciones" && (
          <div className="space-y-3">
            <div className="text-sm text-gray-600 bg-green-50 p-3 rounded border border-green-200">
              <strong>Asesores asignados a esta empresa.</strong> Al activar un switch, el asesor podrá ver esta empresa al crear solicitudes.
            </div>

            {loading ? (
              <div className="text-sm text-gray-500">Cargando asesores...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-green-100">
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border border-gray-200">Asesor</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700 border border-gray-200 w-20">Asignado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asesoresFiltrados.map((u) => (
                      <tr key={u.ownerID}>
                        <td className="px-3 py-2 border border-gray-200">
                          <div className="font-medium text-gray-900">{u.name}</div>
                          <div className="text-xs text-gray-500">{u.ownerID} · {u.region || "—"}</div>
                        </td>
                        <td className="px-3 py-2 border border-gray-200 text-center">
                          <Switch
                            checked={isAssigned(u.ownerID)}
                            disabled={updatingOwner === u.ownerID}
                            onChange={(next) => toggleAsignacion(u.ownerID, next)}
                            color="blue"
                          />
                        </td>
                      </tr>
                    ))}
                    {asesoresFiltrados.length === 0 && (
                      <tr>
                        <td colSpan={2} className="px-3 py-4 text-center text-gray-500 border border-gray-200">
                          No hay asesores activos en esta región.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

